import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, Package, Store, ShieldCheck, Search, 
  CheckCircle2, XCircle, Calendar, ArrowLeft,
  TrendingUp, UserPlus, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, with_products, without_products

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Error al cargar datos</p>
      </div>
    );
  }

  // Filter users
  let filteredUsers = data.users || [];
  
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filteredUsers = filteredUsers.filter(u => 
      u.name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.company_name?.toLowerCase().includes(term)
    );
  }

  if (filter === 'with_products') {
    filteredUsers = filteredUsers.filter(u => u.product_count > 0);
  } else if (filter === 'without_products') {
    filteredUsers = filteredUsers.filter(u => u.product_count === 0);
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  Panel de Admin - Usuarios
                </h1>
                <p className="text-sm text-slate-500">Gestión de usuarios registrados</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">
              Admin
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary.total_users}</p>
                  <p className="text-xs text-slate-500">Total usuarios</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary.users_with_products}</p>
                  <p className="text-xs text-slate-500">Con productos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary.users_without_products}</p>
                  <p className="text-xs text-slate-500">Sin productos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary.total_products}</p>
                  <p className="text-xs text-slate-500">Total productos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email o empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant={filter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('all')}
                >
                  Todos
                </Button>
                <Button 
                  variant={filter === 'with_products' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('with_products')}
                  className={filter === 'with_products' ? 'bg-emerald-600' : ''}
                >
                  Con productos
                </Button>
                <Button 
                  variant={filter === 'without_products' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('without_products')}
                  className={filter === 'without_products' ? 'bg-amber-600' : ''}
                >
                  Sin productos
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Usuarios Registrados</span>
              <Badge variant="outline">{filteredUsers.length} usuarios</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Usuario</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Empresa</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Productos</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Plan</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Tipo</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">Registro</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, i) => (
                    <tr key={u.user_id} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium text-slate-900">{u.name || 'Sin nombre'}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                          {u.phone && <p className="text-xs text-slate-400">{u.phone}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {u.company_name ? (
                          <span className="text-slate-700">{u.company_name}</span>
                        ) : (
                          <span className="text-slate-400 italic">Sin empresa</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {u.product_count > 0 ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {u.product_count}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">
                            0
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge className={
                          u.subscription_plan === 'pro' ? 'bg-purple-100 text-purple-700' :
                          u.subscription_plan === 'oro' ? 'bg-amber-100 text-amber-700' :
                          u.subscription_plan === 'sponsor' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {u.subscription_plan || 'free'}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {u.user_type === 'seller' ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            <Store className="w-3 h-3 mr-1" />
                            Vendedor
                          </Badge>
                        ) : u.user_type === 'buyer' ? (
                          <Badge variant="outline" className="text-blue-600 border-blue-300">
                            Comprador
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Calendar className="w-3 h-3" />
                          <span className="text-xs">{formatDate(u.created_at)}</span>
                        </div>
                        {u.referred_by && (
                          <div className="flex items-center gap-1 mt-1">
                            <UserPlus className="w-3 h-3 text-emerald-500" />
                            <span className="text-xs text-emerald-600">Referido</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No se encontraron usuarios
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUsersPage;
